import '../../../styles/components/Pagination.scss';
import PropTypes from 'prop-types';
import React from 'react';

const PAGINATION_CONSTANTS = {
  MIN_PAGES_FOR_ELLIPSIS: 10,
  END_COUNT: 4,
  MIDDLE_BUFFER_COUNT: 1
};

const EllipsisButton = React.memo(({ testId = "disabledButton" }) => (
  <button type="button" data-testid={testId} className="buttons" disabled>
    ...
  </button>
));
EllipsisButton.displayName = 'EllipsisButton';

const PageButton = React.memo(({ page, isActive, onClick }) => (
  <button
    type="button"
    data-testid="goToPageButton"
    className={`buttons ${isActive ? 'active' : ''}`}
    onClick={onClick}
    aria-label={`Go to page ${page}`}
    aria-current={isActive ? 'page' : undefined}
  >
    {page}
  </button>
));
PageButton.displayName = 'PageButton';

const usePaginationRange = (pagesCount, pageIndex) => {
  if (pagesCount <= PAGINATION_CONSTANTS.MIN_PAGES_FOR_ELLIPSIS) {
    return { range: [{ start: 0, end: pagesCount - 1 }], showStartEllipsis: false, showEndEllipsis: false };
  }

  if (pageIndex < PAGINATION_CONSTANTS.END_COUNT - 1) {
    return {
      range: [
        { start: 0, end: PAGINATION_CONSTANTS.END_COUNT - 1 },
        { start: pagesCount - 1, end: pagesCount - 1 }
      ],
      showStartEllipsis: false,
      showEndEllipsis: true
    };
  }

  if (pageIndex > pagesCount - PAGINATION_CONSTANTS.END_COUNT) {
    return {
      range: [
        { start: 0, end: 0 },
        { start: pagesCount - PAGINATION_CONSTANTS.END_COUNT, end: pagesCount - 1 }
      ],
      showStartEllipsis: true,
      showEndEllipsis: false
    };
  }

  return {
    range: [
      { start: 0, end: 0 },
      { start: Math.max(1, pageIndex - PAGINATION_CONSTANTS.MIDDLE_BUFFER_COUNT), 
        end: Math.min(pagesCount - PAGINATION_CONSTANTS.MIDDLE_BUFFER_COUNT, pageIndex + PAGINATION_CONSTANTS.MIDDLE_BUFFER_COUNT) },
      { start: pagesCount - 1, end: pagesCount - 1 }
    ],
    showStartEllipsis: true,
    showEndEllipsis: true
  };
};

const Pagination = React.memo(({ 
  gotoNextPage, 
  gotoPreviousPage, 
  canNextPage, 
  canPreviousPage, 
  pagesCount, 
  pageIndex, 
  gotoPage 
}) => {
  if (!canNextPage && !canPreviousPage) {
    return null;
  }

  return (
    <div className='paginationWrapper' data-testid="pagination">
      <div className='paginationContainer'>
        <NextPrevButton
          onClick={gotoPreviousPage}
          disabled={!canPreviousPage}
        />
        {getAllPagesLink(pagesCount, pageIndex, gotoPage)}
        <NextPrevButton
          isNext
          onClick={gotoNextPage}
          disabled={!canNextPage}
        />
      </div>
    </div>
  );
});
Pagination.displayName = 'Pagination';

const generateLinks = (start, end, pageIndex, gotoPage) => {
  const pageLinks = [];
  for (let i = start; i <= end; i++) {
    pageLinks.push(
      <PageButton
        key={i}
        page={i + 1}
        isActive={i === pageIndex}
        onClick={() => gotoPage(i)}
      />
    );
  }
  return pageLinks;
};

const getAllPagesLink = (pagesCount, pageIndex, gotoPage) => {
  const { range, showStartEllipsis, showEndEllipsis } = usePaginationRange(pagesCount, pageIndex);
  
  return range.reduce((acc, { start, end }, index) => {
    if (showStartEllipsis && index === 1) {
      acc.push(<EllipsisButton key="ellipsis1" />);
    }
    
    acc.push(generateLinks(start, end, pageIndex, gotoPage));
    
    if (showEndEllipsis && index === range.length - 2) {
      acc.push(<EllipsisButton key="ellipsis2" />);
    }
    
    return acc;
  }, []);
};

const NextPrevButton = ({ onClick, disabled, isNext = false }) => {
    return (
      <button
        type="button"
        className={isNext ? 'nextButton' : 'prevButton'}
        data-testid={isNext ? "nextButton" : "prevButton"}
        onClick={onClick}
        disabled={disabled}
      >
        { !isNext && <ChevronLeft /> }
        { isNext && <Chevron /> }
      </button>
    );
};

const Chevron = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    className="chevron"
    viewBox="0 0 16 16"
  >
    <path
      fillRule="evenodd"
      d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
    />
    <path
      fillRule="evenodd"
      d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
      transform="translate(5, 0)"
    />
  </svg>
);

const ChevronLeft = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    className="chevron"
    viewBox="0 0 16 16"
  >
    <path
      fillRule="evenodd"
      d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
    />
    <path
      fillRule="evenodd"
      d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
      transform="translate(-5, 0)"
    />
  </svg>
);

Pagination.propTypes = {
  gotoNextPage: PropTypes.func.isRequired,
  gotoPreviousPage: PropTypes.func.isRequired,
  canNextPage: PropTypes.bool.isRequired,
  canPreviousPage: PropTypes.bool.isRequired,
  pagesCount: PropTypes.number.isRequired,
  pageIndex: PropTypes.number.isRequired,
  gotoPage: PropTypes.func.isRequired,
};

export default Pagination;
